// TeamCalendar.jsx - Fixed version
import React, { memo } from "react";
import styled from "@emotion/styled";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import enUS from "date-fns/locale/en-US";
import { Box, Typography, Paper, alpha } from "@mui/material";
import { CalendarToday as CalendarTodayIcon } from "@mui/icons-material";

// Setup localizer for react-big-calendar
const locales = {
  "en-US": enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Styled Components for enhanced UI
const StyledCalendar = styled(Calendar)(({ theme }) => ({
  "& .rbc-header": {
    padding: "10px 3px",
    fontWeight: 600,
    fontSize: "0.85rem",
  },
  "& .rbc-month-view": {
    borderRadius: 8,
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)",
    border: `1px solid ${theme.palette.divider}`,
  },
  // Make entire date cell clickable with pointer cursor
  "& .rbc-day-bg": {
    cursor: "pointer",
  },
  "& .rbc-date-cell": {
    cursor: "pointer",
  },
  "& .rbc-day-bg:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.08),
  },
  "& .rbc-today": {
    backgroundColor: alpha(theme.palette.primary.light, 0.12),
  },
  "& .rbc-event": {
    borderRadius: 4,
    padding: "2px 5px",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12)",
    transition: "transform 0.2s ease",
    fontSize: "0.75rem",
    cursor: "pointer",
  },
  "& .rbc-event:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 3px 5px rgba(0, 0, 0, 0.2)",
  },
  // Ensure all interactive elements show pointer
  "& .rbc-row-content": {
    cursor: "pointer",
  },
  "& .rbc-row-bg": {
    cursor: "pointer",
  },
}));

/**
 * TeamCalendar Component
 *
 * Displays a calendar with bookings for the selected member
 */
const TeamCalendar = memo(
  ({ calendarEvents, selectedDate, handleSelectDate, theme }) => {
    // Prevent selection of dates in the past
    const isDateSelectable = (date) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date >= today;
    };

    // Handle date navigation in the calendar
    const handleNavigate = (newDate) => {
      // Make a copy to avoid date reference issues
      handleSelectDate(new Date(newDate));
    };

    // Handle slot selection by the user
    const handleSelectSlot = ({ start }) => {
      if (isDateSelectable(start)) {
        // Make a copy to avoid date reference issues
        handleSelectDate(new Date(start));
      }
    };

    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          mb: 3,
        }}
      >
        <Box display="flex" alignItems="center" mb={3}>
          <CalendarTodayIcon
            sx={{ mr: 1.5, color: theme.palette.primary.main }}
          />
          <Typography variant="h6" fontWeight={600}>
            Team Calendar
          </Typography>
        </Box>

        <Box height="400px">
          <StyledCalendar
            localizer={localizer}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            defaultDate={selectedDate}
            date={selectedDate}
            style={{ height: "100%" }}
            onNavigate={handleNavigate}
            onSelectSlot={handleSelectSlot}
            selectable
            popup
            views={["month"]}
            defaultView="month"
            eventPropGetter={(event) => ({
              style: {
                backgroundColor: event.resource?.isBooked
                  ? theme.palette.error.main
                  : theme.palette.success.main,
                color: "#fff",
                border: "none",
              },
            })}
            theme={theme}
          />
        </Box>
      </Paper>
    );
  }
);

export default TeamCalendar;
